import { useCallback } from 'react';
import { useLogging, LogLevel, LogCategory } from '../context/logging-context';

/**
 * Hook personalizado para facilitar el logging de acciones en componentes
 * 
 * Uso:
 * const logAction = useActionLogger('orders');
 * logAction.info('ORDER_CREATED', 'Pedido creado exitosamente', { orderId: '123' });
 * logAction.error('ORDER_FAILED', 'Error al crear pedido', { error: errorMessage });
 */
export function useActionLogger(category: LogCategory) {
  const { log } = useLogging();

  const logInfo = useCallback((
    action: string,
    description: string,
    metadata?: Record<string, any>
  ) => {
    log('info', category, action, description, metadata);
  }, [log, category]);

  const logWarning = useCallback((
    action: string,
    description: string,
    metadata?: Record<string, any>
  ) => {
    log('warning', category, action, description, metadata);
  }, [log, category]);

  const logError = useCallback((
    action: string,
    description: string,
    metadata?: Record<string, any>
  ) => {
    log('error', category, action, description, metadata);
  }, [log, category]);

  const logDebug = useCallback((
    action: string,
    description: string,
    metadata?: Record<string, any>
  ) => {
    log('debug', category, action, description, metadata);
  }, [log, category]);

  const logCritical = useCallback((
    action: string,
    description: string,
    metadata?: Record<string, any>
  ) => {
    log('critical', category, action, description, metadata);
  }, [log, category]);

  // Función para trackear operaciones con tiempo de ejecución
  const trackOperation = useCallback(async <T,>(
    action: string,
    description: string,
    operation: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> => {
    const startTime = performance.now();
    
    try {
      logDebug(action + '_START', `Iniciando: ${description}`, metadata);
      const result = await operation();
      const duration = performance.now() - startTime;
      
      logInfo(action + '_SUCCESS', `Completado: ${description}`, {
        ...metadata,
        duration: `${duration.toFixed(2)}ms`,
        success: true,
      });
      
      return result;
    } catch (error: any) {
      const duration = performance.now() - startTime;
      
      logError(action + '_ERROR', `Error: ${description} - ${error.message}`, {
        ...metadata,
        duration: `${duration.toFixed(2)}ms`,
        error: error.message,
        errorStack: error.stack,
        success: false,
      });
      
      throw error;
    }
  }, [logDebug, logInfo, logError]);

  // Función para trackear cambios de estado
  const trackStateChange = useCallback((
    action: string,
    description: string,
    previousState: any,
    newState: any,
    metadata?: Record<string, any>
  ) => {
    log('info', category, action, description, {
      ...metadata,
      previousState,
      newState,
      changed: JSON.stringify(previousState) !== JSON.stringify(newState),
    });
  }, [log, category]);

  return {
    info: logInfo,
    warning: logWarning,
    error: logError,
    debug: logDebug,
    critical: logCritical,
    trackOperation,
    trackStateChange,
  };
}
